import type { IMedication } from '../../../../interfaces/medication';
import type { IEpisodeContext } from '../../../episodes/EpisodeContext';
import type { ICustomControlRequestAddEditProps } from '../../interfaces/';
import type { IKsyosDesktopActionLabels } from '../../utils/v1/AssetDropzoneWithActions';
import type { ServiceConsumerProp } from '../../utils/v1/loadServiceConsumer';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { FilesUrlBase } from '../../../../../../common/enums/filesUrlBase';
import { PractitionerRoleInRequest } from '../../../../../../common/enums/practitionerRoleInRequest';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { ToastType } from '../../../../../../common/enums/toastType';
import { getJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { checkPreviousExaminationHasAssets } from '../../../../../utils/checkPreviousExaminationHasAssets';
import { useEpisodeContext } from '../../../../../utils/hooks/useEpisodeContext';
import { showServiceToast } from '../../../../../utils/showToast';
import { CancelButton } from '../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer, Form, FormDivider, HeaderDivider } from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { ProceedWithoutFiles } from '../../../../elements/ProceedWithoutFiles';
import { EvaluationRequestItem } from '../../../evaluation/EvaluationRequestItem';
import { ProfessionalSummaryData } from '../../../professionalSummaryData/ProfessionalSummaryData';
import { AssetDropzoneWithActions } from '../../utils/v1/AssetDropzoneWithActions';
import { CareProviderSelector } from '../../utils/v1/CareProviderSelector';
import { DiagnosisConclusionSelection } from '../../utils/v1/DiagnosisConclusionSelection';
import { useLoadServiceConsumer } from '../../utils/v1/loadServiceConsumer';
import { MedicationsAddEdit } from '../../utils/v1/medications/MedicationsAddEdit';
import { NewOrEditRequestProcessContainer } from '../../utils/v1/NewOrEditRequestProcessContainer';
import { Question } from '../../utils/v1/Question';
import { ServiceConsumerSupervisorSelector } from '../../utils/v1/ServiceConsumerSupervisorSelector';
import { ServiceOrganizationProvider } from '../../utils/v1/ServiceOrganizationProvider';
import { SpirometryAddEdit } from './SpirometryAddEdit';
import { TpcRequestFollowingSteps } from './TpcRequestFollowingSteps';

interface IInnerTpcV1RequestAddEditState {
    buttonDisabled?: boolean;
    isProcessing?: boolean;
    proceedWithoutFiles?: boolean;
    showEvaluation?: boolean;
    showProceedWithoutFiles?: boolean;
    showSpirometerModal?: boolean;
}

export function TpcV1RequestAddEdit(props: ICustomControlRequestAddEditProps) {
    const serviceConsumer = useLoadServiceConsumer(props.service.id, { employmentId: props.employmentId });
    const episodeContext = useEpisodeContext();

    return (
        <InnerTpcV1RequestAddEdit
            {...props}
            serviceConsumer={serviceConsumer}
            {...episodeContext}
        />
    );
}

class InnerTpcV1RequestAddEdit extends Component<
    ICustomControlRequestAddEditProps & ServiceConsumerProp & IEpisodeContext,
    IInnerTpcV1RequestAddEditState
> {
    public render() {
        return (
            <NewOrEditRequestProcessContainer
                isFirstItem={this.props.isFirstItem}
                itemState={this.props.itemState}
                process={this.props.process}
                service={this.props.service}
                title={getServiceLabel(this.props.service.code, 'request', this.props.service.name)}
            >
                {this.addEditForm()}
            </NewOrEditRequestProcessContainer>
        );
    }

    private addEditForm() {
        return (
            <Form
                onSubmit={async () => null}
                showOverlay={this.state.isProcessing}
            >
                {this.state.showEvaluation && this.props.request.id && (
                    <EvaluationRequestItem
                        onClose={async () => {
                            await this.setState({ showEvaluation: false });
                            const result = await getJson(
                                `/api/organizations/${this.props.request.selectedDiagnosticReportProviderOrganizationId}`
                            );
                            showServiceToast(
                                ToastType.success,
                                'toast.submitSuccess',
                                this.props.service,
                                'request',
                                translateKey('toast.consultRequestSubmit', result.json.name)
                            );
                            await this.props.onSave(this.props.request.id!);
                        }}
                        requestId={this.props.request.id}
                    />
                )}

                {this.state.showProceedWithoutFiles && (
                    <ProceedWithoutFiles
                        entity={ProcessEntity.request}
                        onNo={async () =>
                            await this.setState({ showProceedWithoutFiles: false, buttonDisabled: false })
                        }
                        onYes={async () => {
                            await this.setState({ showProceedWithoutFiles: false, proceedWithoutFiles: true });
                            await this.saveRequest('final');
                        }}
                        serviceType={this.props.service.serviceType}
                    />
                )}

                <CareProviderSelector
                    {...this.props}
                    entity={this.props.request}
                />

                <ServiceOrganizationProvider
                    errors={this.props.errors}
                    initialOrganizationId={this.props.request.selectedDiagnosticReportProviderOrganizationId}
                    initialPractitionerId={this.props.request.selectedDiagnosticReportProviderPractitionerId}
                    onChange={async (organizationId, practitionerId) => {
                        await this.props.handleChange('selectedDiagnosticReportProviderOrganizationId', organizationId);
                        await this.props.handleChange('selectedDiagnosticReportProviderPractitionerId', practitionerId);
                    }}
                    onDoValidate={async () =>
                        await this.props.validateOne('selectedDiagnosticReportProviderOrganizationId')
                    }
                    roleOfPractitionersInList={PractitionerRoleInRequest.specialist}
                    searchInNetwork={true}
                    serviceProviderServiceId={this.props.service.id}
                    serviceType={this.props.service.serviceType}
                />

                <ServiceConsumerSupervisorSelector {...this.props} />

                <FormDivider />
                <HeaderDivider text={translateKey('procedure.procedure')} />

                <AssetDropzoneWithActions
                    assets={this.props.request.assets}
                    assetsBaseUrl={FilesUrlBase.requests}
                    ksyosDesktopActionLabels={this.getKsyosDesktopActionLabels()}
                    onChange={async (assets) => await this.props.handleChange('assets', assets)}
                    onClickStartKsyosDesktop={async () => await this.setState({ showSpirometerModal: true })}
                    onUploading={async (isUploading) => await this.setState({ buttonDisabled: isUploading })}
                    processActivityId={this.props.request.id}
                    service={this.props.service}
                    showAdditionalFilesCheckbox={this.props.previousRequest !== undefined}
                    updateNewRequestPreviousRequest={this.props.updateNewRequestPreviousRequest}
                />

                <SpirometryAddEdit
                    afterShowSpirometerModal={async () => await this.setState({ showSpirometerModal: false })}
                    customControlData={this.props.request.customControlData}
                    employmentId={this.props.employmentId}
                    errors={this.props.errors}
                    handleChange={this.props.handleChangeCustomControl}
                    organizationId={this.props.organizationId}
                    patientId={this.props.patient.id}
                    pulmoType="request"
                    requestId={this.props.request.id}
                    saveAsDraft={async () => await this.saveForStartSpirometry()}
                    serviceConsumer={this.props.serviceConsumer}
                    serviceId={this.props.service.id}
                    setIsProcessing={async (isProcessing) => await this.setState({ isProcessing })}
                    showSpirometerModal={this.state.showSpirometerModal}
                    validateOne={this.props.validateOneCustomControl}
                />

                <MedicationsAddEdit
                    entity={ProcessEntity.request}
                    entityId={this.props.request.id}
                    errors={this.props.errors}
                    handleChangePatientUsesMedication={async (patientUsesMedication: boolean) =>
                        await this.props.handleChange('patientUsesMedication', patientUsesMedication)
                    }
                    onChange={async (medications: IMedication[]) =>
                        await this.props.handleChange('requestMedications', medications)
                    }
                    patientUsesMedication={this.props.request.patientUsesMedication}
                    validateMedications={async () => await this.props.validateOne('requestMedications')}
                    validatePatientUsesMedication={async () => await this.props.validateOne('patientUsesMedication')}
                />

                <ProfessionalSummaryData
                    entity={ProcessEntity.request}
                    entityId={this.props.request.id}
                    errors={this.props.errors}
                    handleChangeAllergies={async (allergies) => await this.props.handleChange('allergies', allergies)}
                    handleChangeEpisodes={async (episodesAndJournal) =>
                        await this.props.handleChange('requestEpisodesAndJournal', episodesAndJournal)
                    }
                    handleChangeLabResults={async (labResults) =>
                        await this.props.handleChange('requestLabResults', labResults)
                    }
                    validateEpisodesAndJournals={async () => await this.props.validateOne('requestEpisodesAndJournal')}
                />

                <DiagnosisConclusionSelection
                    conclusie_diagnose={this.props.request.customControlData.conclusie_diagnose}
                    diagnosis={this.props.request.diagnosis}
                    errors={this.props.errors}
                    handleChangeConclusieDiagnose={this.props.handleChangeCustomControl}
                    handleChangeDiagnosisConsideration={async (diagnosis) =>
                        await this.props.handleChange('diagnosis', diagnosis)
                    }
                    service={this.props.service}
                    validateConclusieDiagnose={this.props.validateOneCustomControl}
                    validateDiagnosisConsideration={async () => this.props.validateOne('diagnosis')}
                />

                <Question
                    automation="question"
                    errors={this.props.errors}
                    onChange={async (value) => await this.props.handleChange('question', value)}
                    onDoValidate={async () => await this.props.validateOne('question')}
                    question={this.props.request.question}
                />

                <TpcRequestFollowingSteps />

                <ButtonContainer
                    alignRight
                    buttons={[
                        <CancelButton
                            cancel={this.props.onCancel}
                            entity={ProcessEntity.request}
                            isExisting={!!this.props.request.id}
                            serviceCode={this.props.service.code}
                            serviceType={this.props.service.serviceType}
                        />,
                        <SaveButton
                            buttonDisabled={this.state.buttonDisabled}
                            save={async () => {
                                await this.saveRequest('draft');
                            }}
                        />,
                        <SaveSendButton
                            buttonDisabled={this.state.buttonDisabled}
                            saveSend={async () => {
                                await this.saveRequest('final');
                            }}
                        />,
                    ]}
                />
            </Form>
        );
    }

    private getKsyosDesktopActionLabels(): IKsyosDesktopActionLabels | undefined {
        if (this.props.serviceConsumer && this.props.serviceConsumer.usingSpirometer) {
            return {
                titleWhenOnlyAction: translateKey('upload.orStartTheSpirometer'),
                linkTextWhenOnlyAction: translateKey('upload.viaKsyosDesktop'),
                linkTextWhenMultipleActions: translateKey('upload.startSpirogram'),
            };
        }

        return undefined;
    }

    private async saveRequest(saveType: 'draft' | 'final') {
        await this.setState({ buttonDisabled: true });

        const validatedRequest = await this.props.validateRequest(saveType);
        if (!validatedRequest) {
            await this.setState({ buttonDisabled: false });

            return;
        }

        if (
            !this.state.proceedWithoutFiles &&
            saveType === 'final' &&
            this.props.request.assets.length === 0 &&
            (!this.props.request.customControlData.files || this.props.request.customControlData.files.length === 0) &&
            !checkPreviousExaminationHasAssets(this.props.previousRequest?.procedure)
        ) {
            return await this.setState({ showProceedWithoutFiles: true });
        }

        const requestId = await this.props.onSaveRequest(saveType);
        await this.setState({ buttonDisabled: false });

        if (saveType === 'final') {
            await this.setState({ showEvaluation: true });
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                this.props.service,
                'request',
                translateKey('toast.draftDefaultMessage')
            );
            await this.props.onSave(requestId);
        }
    }

    private async saveForStartSpirometry() {
        await this.setState({ buttonDisabled: true });

        const validatedRequest = await this.props.validateRequest('draft');
        if (!validatedRequest) {
            await this.setState({ buttonDisabled: false });

            return;
        }
        const requestId = await this.props.onSaveRequest('draft');
        await this.setState({ buttonDisabled: false });

        return requestId;
    }
}
